import { useMemo } from "react";
import type { Theme } from "@mui/material";
import {
  Assignment,
  LocalHospital,
  MedicalServices,
  Medication,
  NoteAddOutlined,
  ShowChart,
} from "@mui/icons-material";
import { getAnalysisAccentColor } from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";
import type { AnalysisCardConfig } from "./analysisCardTypes";

export function useAnalysisCardGroups(theme: Theme) {
  const treatmentGroupCards: AnalysisCardConfig[] = useMemo(
    () => [
      {
        active: true,
        id: "tratamentos",
        title: "Prescrições",
        description:
          "Medicamentos mais prescritos e padrões de tratamento na organização.",
        icon: Medication,
        color: getAnalysisAccentColor(theme, "tratamentos"),
      },
      {
        active: true,
        id: "protocolos",
        title: "Protocolos",
        description: "Protocolos mais aplicados e padronização de condutas.",
        icon: Assignment,
        color: getAnalysisAccentColor(theme, "protocolos"),
      },
      {
        active: true,
        id: "condutas",
        title: "Condutas",
        description: "Situação das condutas e gargalos no fluxo de atendimento.",
        icon: LocalHospital,
        color: getAnalysisAccentColor(theme, "condutas"),
      },
      {
        active: true,
        id: "problemas",
        title: "Problemas",
        description: "Evolução dos problemas e foco nos casos mais críticos.",
        icon: ShowChart,
        color: getAnalysisAccentColor(theme, "problemas"),
      },
      {
        active: true,
        id: "planos",
        title: "Planos",
        description: "Planos terapêuticos e progresso dos pacientes.",
        icon: ShowChart,
        color: getAnalysisAccentColor(theme, "planos"),
      },
    ],
    [theme]
  );

  const diagnosticsGroupCards: AnalysisCardConfig[] = useMemo(
    () => [
      {
        active: true,
        id: "diagnosticos",
        title: "Diagnósticos",
        description: "Diagnósticos mais frequentes e ações preventivas.",
        icon: MedicalServices,
        color: getAnalysisAccentColor(theme, "diagnosticos"),
      },
      {
        active: true,
        id: "service",
        title: "Exames",
        description: "Exames mais solicitados e uso de recursos diagnósticos.",
        icon: Medication,
        color: getAnalysisAccentColor(theme, "service"),
      },
      {
        active: true,
        id: "alergias",
        title: "Alergias",
        description: "Alergias frequentes e menor risco nas consultas.",
        icon: NoteAddOutlined,
        color: getAnalysisAccentColor(theme, "alergias"),
      },
    ],
    [theme]
  );

  const cardTitleById = useMemo(() => {
    const map = new Map<string, string>();
    [...treatmentGroupCards, ...diagnosticsGroupCards].forEach((card) => {
      map.set(card.id, card.title);
    });
    return map;
  }, [treatmentGroupCards, diagnosticsGroupCards]);

  return { treatmentGroupCards, diagnosticsGroupCards, cardTitleById };
}
